import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const API_TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS || 60000)
const API_MAX_RETRIES = Number(import.meta.env.VITE_API_RETRIES || 2)
const API_RETRY_BASE_MS = Number(import.meta.env.VITE_API_RETRY_BASE_MS || 300)

const api = axios.create({ baseURL: API_BASE_URL, timeout: API_TIMEOUT_MS })

const RETRYABLE_METHODS = new Set(['get', 'head', 'options'])

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function getApiErrorMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    'Request failed'
  )
}

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status
    const method = (error?.config?.method || '').toLowerCase()

    if (status === 401) {
      localStorage.removeItem('token')
      window.dispatchEvent(new CustomEvent('api:unauthorized'))
    }

    const config = error?.config
    const retryableStatus = status === 429 || (status >= 500 && status <= 599)
    const retryable = config && RETRYABLE_METHODS.has(method) && (!status || retryableStatus)

    if (retryable) {
      config.__retryCount = config.__retryCount || 0
      if (config.__retryCount < API_MAX_RETRIES) {
        config.__retryCount += 1
        const backoff = API_RETRY_BASE_MS * (2 ** (config.__retryCount - 1))
        await sleep(backoff)
        return api(config)
      }
    }

    error.friendlyMessage = getApiErrorMessage(error)
    return Promise.reject(error)
  }
)

export const getStatus = () => api.get('/status')

// ── Auth ──────────────────────────────────────────────────────────────────────
// 🪤 Updated to accept the third honeypot argument
export const login = (email, password, loginConfirmField = '') => {
  const params = new URLSearchParams()
  params.append('username', email)
  params.append('password', password)

  // If the trap was triggered, add it to the params
  if (loginConfirmField) {
    params.append('login_confirm_field', loginConfirmField)
  }

  return api.post('/auth/login', params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  })
}
export const register = (data)  => api.post('/auth/register', data)
export const verifyEmail = (token) => api.get('/auth/verify-email', { params: { token } })
export const resendVerification = (email) => api.post('/auth/resend-verification', { email })
export const getMe    = ()      => api.get('/auth/me')
export const updateProfile  = (data) => api.patch('/auth/me', data)
export const changePassword = (data) => api.post('/auth/change-password', data)
export const deleteUserAccount = ()  => api.delete('/auth/me')

// ── Subscription ──────────────────────────────────────────────────────────────
// tier = 'pro' | 'edge'   annual = true/false passed as query param
export const createCheckoutSession = (tier, annual = false) =>
  api.post(`/subscription/checkout/${tier}`, null, { params: { annual } })
export const createPortalSession   = () => api.post('/subscription/portal')

// ── Accounts (trading accounts, not user auth) ────────────────────────────────
export const getAccounts       = ()         => api.get('/accounts')
export const createAccount     = (data)     => api.post('/accounts', data)
export const updateAccount     = (id, data) => api.put(`/accounts/${id}`, data)
export const setDefaultAccount = (id)       => api.post(`/accounts/${id}/set-default`)
export const deleteAccount     = (id)       => api.delete(`/accounts/${id}`)

// ── Trades ────────────────────────────────────────────────────────────────────
export const getTrades        = (params) => api.get('/trades', { params })
export const getTrade         = (id)     => api.get(`/trades/${id}`)
export const deleteTrade      = (id)     => api.delete(`/trades/${id}`)
export const deleteTradesBulk = (ids)    => api.delete('/trades', { data: { ids, confirm_all: ids === null } })
export const assignTradeAccount = (id, account_id = null) =>
  api.patch(`/trades/${id}/account`, { account_id })
export const assignTradesAccountBulk = (ids, account_id = null) =>
  api.patch('/trades/account', { ids, account_id })

// ── Journal ───────────────────────────────────────────────────────────────────
export const saveJournal = (id, data) => api.post(`/trades/${id}/journal`, data)
export const getJournal  = (id)       => api.get(`/trades/${id}/journal`)

// ── AI ────────────────────────────────────────────────────────────────────────
export const analyseTrade     = (id)                      => api.post(`/trades/${id}/ai-analysis`)
export const getTradeAnalysis = (id)                      => api.get(`/trades/${id}/ai-analysis`)
export const runFullAnalysis  = (account_id = null)       =>
  api.post('/ai/full-analysis', null, { params: { ...(account_id && { account_id }) } })
export const aiChat           = (message, history, account_id = null) =>
  api.post('/ai/chat', { message, history }, { params: { ...(account_id && { account_id }) } })
export const getAiUsage       = () => api.get('/ai/usage')

// ── Metrics ───────────────────────────────────────────────────────────────────
export const getMetrics        = (days = 30, account_id = null) =>
  api.get('/metrics/summary', { params: { days, ...(account_id && { account_id }) } })
export const getPnlCurve       = (days = 90, account_id = null) =>
  api.get('/metrics/pnl-curve', { params: { days, ...(account_id && { account_id }) } })
export const getMetricsBySetup = (account_id = null) =>
  api.get('/metrics/by-setup', { params: { ...(account_id && { account_id }) } })
export const getMetricsByHour  = (account_id = null) =>
  api.get('/metrics/by-hour', { params: { ...(account_id && { account_id }) } })
export const getMetricsByDay   = (account_id = null) =>
  api.get('/metrics/by-day-of-week', { params: { ...(account_id && { account_id }) } })

// ── Setup tags ────────────────────────────────────────────────────────────────
export const getSetupTags   = ()     => api.get('/setup-tags')
export const createSetupTag = (data) => api.post('/setup-tags', data)
export const deleteSetupTag = (name) => api.delete(`/setup-tags/${name}`)

// ── Import ────────────────────────────────────────────────────────────────────
export const uploadFile = (file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/import/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const confirmImport = (session_id, trades, account_id = null) =>
  api.post('/import/confirm', { session_id, trades, account_id })
export const syncTrades = (data) => api.post('/import/sync', data)

export default api
export { getApiErrorMessage }
